import {
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";

const STEPS = { REQUEST: "request", EMAIL_SENT: "emailSent", RESET: "reset" };

const inputStyles = {
  size: "lg",
  h: "clamp(44px, 4vh, 52px)",
  borderRadius: "12px",
  border: "1px solid #DADADA",
  _placeholder: { color: "#9E9E9E" },
  _hover: { borderColor: "#D0D0D0" },
  _focus: { borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" },
};

const labelStyles = {
  fontSize: "clamp(14px, 1vw, 16px)",
  fontWeight: "semibold",
  color: "#4F4F4F",
  mb: "clamp(6px, 0.5vw, 8px)",
};

const primaryButtonStyles = {
  bg: "#CF008A",
  color: "white",
  size: "lg",
  h: "clamp(48px, 4.5vh, 56px)",
  fontSize: "clamp(15px, 1.1vw, 17px)",
  fontWeight: "semibold",
  borderRadius: "12px",
  _hover: { bg: "#D933A1" },
  _active: { bg: "#B8007A" },
};

export default function ForgotPasswordDialog({ isOpen, onClose }) {
  const [step, setStep] = useState(STEPS.REQUEST);
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClose = () => {
    setStep(STEPS.REQUEST);
    setEmail("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const handleRequestSubmit = (e) => {
    e.preventDefault();
    setStep(STEPS.EMAIL_SENT);
  };

  const handleResetSubmit = (e) => {
    e.preventDefault();
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxW="clamp(320px, 90vw, 440px)"
        borderRadius="20px"
        overflow="hidden"
        boxShadow="xl"
        bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
        border="1px solid #ECECEC"
      >
        <ModalBody py="clamp(24px, 3vw, 36px)" px="clamp(24px, 3vw, 32px)">
          <VStack spacing="clamp(20px, 2.5vw, 28px)" align="stretch">
            {step === STEPS.REQUEST && (
              <>
                <Text
                  fontSize="clamp(24px, 2.2vw, 32px)"
                  fontWeight="semibold"
                  color="#242424"
                  textAlign="center"
                  lineHeight="1.25"
                >
                  Forgot password?
                </Text>
                <Text
                  fontSize="clamp(14px, 1.1vw, 16px)"
                  color="#6D6D6D"
                  textAlign="center"
                  lineHeight="1.5"
                >
                  Enter your email address and we’ll send you a link to reset your password.
                </Text>

                <VStack
                  as="form"
                  onSubmit={handleRequestSubmit}
                  spacing="clamp(16px, 1.5vw, 20px)"
                  align="stretch"
                >
                  <FormControl isRequired>
                    <FormLabel {...labelStyles}>Email address</FormLabel>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      {...inputStyles}
                    />
                  </FormControl>
                  <Button type="submit" w="100%" {...primaryButtonStyles}>
                    Send reset link
                  </Button>
                </VStack>

                <Button
                  variant="ghost"
                  fontSize="clamp(14px, 1vw, 16px)"
                  color="#CF008A"
                  fontWeight="semibold"
                  _hover={{ color: "#D933A1", bg: "transparent" }}
                  onClick={handleClose}
                >
                  Back to Sign in
                </Button>
              </>
            )}

            {step === STEPS.EMAIL_SENT && (
              <>
                <Text
                  fontSize="clamp(24px, 2.2vw, 32px)"
                  fontWeight="semibold"
                  color="#242424"
                  textAlign="center"
                  lineHeight="1.25"
                >
                  Check your email
                </Text>
                <Text
                  fontSize="clamp(14px, 1.1vw, 16px)"
                  color="#6D6D6D"
                  textAlign="center"
                  lineHeight="1.5"
                >
                  We’ve sent a password reset link to{" "}
                  <Text as="span" fontWeight="semibold" color="#242424">
                    {email || "your email"}
                  </Text>
                  . Click the link to set a new password.
                </Text>

                <Button
                  variant="ghost"
                  fontSize="clamp(14px, 1vw, 16px)"
                  color="#CF008A"
                  fontWeight="semibold"
                  _hover={{ color: "#D933A1", bg: "transparent" }}
                  onClick={() => setStep(STEPS.RESET)}
                >
                  Set new password
                </Button>

                <VStack spacing="clamp(12px, 1vw, 16px)" align="stretch">
                  <Button
                    variant="outline"
                    w="100%"
                    h="clamp(44px, 4vh, 52px)"
                    fontSize="clamp(14px, 1vw, 16px)"
                    fontWeight="semibold"
                    borderRadius="12px"
                    borderColor="#DADADA"
                    color="#242424"
                    _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
                    onClick={() => {}}
                  >
                    Resend link
                  </Button>
                  <Button w="100%" {...primaryButtonStyles} onClick={handleClose}>
                    Back to Sign in
                  </Button>
                </VStack>
              </>
            )}

            {step === STEPS.RESET && (
              <>
                <Text
                  fontSize="clamp(24px, 2.2vw, 32px)"
                  fontWeight="semibold"
                  color="#242424"
                  textAlign="center"
                  lineHeight="1.25"
                >
                  Create new password
                </Text>
                <Text
                  fontSize="clamp(14px, 1.1vw, 16px)"
                  color="#6D6D6D"
                  textAlign="center"
                  lineHeight="1.5"
                >
                  Your new password must be different from previously used passwords.
                </Text>

                <VStack
                  as="form"
                  onSubmit={handleResetSubmit}
                  spacing="clamp(16px, 1.5vw, 20px)"
                  align="stretch"
                >
                  <FormControl isRequired>
                    <FormLabel {...labelStyles}>New password</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        {...inputStyles}
                      />
                      <InputRightElement h="clamp(44px, 4vh, 52px)">
                        <Button
                          variant="ghost"
                          size="sm"
                          fontSize="clamp(12px, 0.9vw, 14px)"
                          color="#6D6D6D"
                          _hover={{ bg: "#F5F5F5" }}
                          onClick={() => setShowPassword((p) => !p)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel {...labelStyles}>Confirm password</FormLabel>
                    <InputGroup size="lg">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        {...inputStyles}
                      />
                      <InputRightElement h="clamp(44px, 4vh, 52px)">
                        <Button
                          variant="ghost"
                          size="sm"
                          fontSize="clamp(12px, 0.9vw, 14px)"
                          color="#6D6D6D"
                          _hover={{ bg: "#F5F5F5" }}
                          onClick={() => setShowConfirmPassword((p) => !p)}
                        >
                          {showConfirmPassword ? "Hide" : "Show"}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <Button type="submit" w="100%" {...primaryButtonStyles}>
                    Reset password
                  </Button>
                </VStack>

                <Button
                  variant="ghost"
                  fontSize="clamp(14px, 1vw, 16px)"
                  color="#CF008A"
                  fontWeight="semibold"
                  _hover={{ color: "#D933A1", bg: "transparent" }}
                  onClick={handleClose}
                >
                  Back to Sign in
                </Button>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
